/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Live polling for a cross-household recipe discussion.
 *
 * Deliberately much simpler than useShoppingSync. That hook needs an offline
 * outbox because ticking an item in a freezer aisle has to work with the radio
 * off. This one does not: a comment written offline and posted twenty minutes
 * later lands in the middle of a conversation that has moved on, which is worse
 * than being told the post failed. So writes go straight to the server and
 * report their own errors.
 *
 * What it shares with the shopping hook is the part that matters — `?since=`
 * deltas against a monotonic revision, and tombstones applied as REMOVALS so a
 * withdrawn comment actually leaves the screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ThreadsApi } from "../../api/endpoints.ts";
import type { ThreadComment, ThreadParticipant, ThreadSummary } from "../../api/types.ts";

/** A conversation, not a shopping trip. 5s is plenty and costs almost nothing. */
const POLL_FOREGROUND_MS = 5_000;
const POLL_BACKGROUND_MS = 60_000;

export interface ThreadSync {
  comments: ThreadComment[];
  participants: ThreadParticipant[];
  summary: ThreadSummary | null;
  loading: boolean;
  error: string | null;
  /** True once we've been told we're no longer allowed to read this. */
  revoked: boolean;
  refresh: () => void;
}

export function useThreadSync(recipeId: string, enabled: boolean): ThreadSync {
  const [comments, setComments] = useState<ThreadComment[]>([]);
  const [participants, setParticipants] = useState<ThreadParticipant[]>([]);
  const [summary, setSummary] = useState<ThreadSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoked, setRevoked] = useState(false);

  // Revision lives in a ref, not state: the poll loop reads it on every tick
  // and re-creating the loop on each new comment would reset the timer.
  const revision = useRef(0);
  const inflight = useRef(false);

  const poll = useCallback(async () => {
    if (!enabled || inflight.current) return;
    inflight.current = true;
    try {
      const delta = await ThreadsApi.sync(recipeId, revision.current);
      setSummary(delta.summary);
      setParticipants(delta.participants);

      setComments((prev) => {
        // A full snapshot replaces; a delta merges. Merging a snapshot would
        // keep comments the server has since dropped from the window.
        const base = delta.isFullSnapshot ? [] : prev;
        const byId = new Map(base.map((c) => [c.id, c]));
        for (const c of delta.comments) byId.set(c.id, c);
        for (const id of delta.tombstones) byId.delete(id);
        return [...byId.values()].sort((a, b) => a.revision - b.revision);
      });

      revision.current = delta.revision;
      setError(null);
    } catch (err) {
      const status = (err as { status?: number }).status;
      // 403 = we left or were removed; 404 = our copy of the recipe is gone.
      // Both mean stop polling rather than retrying forever.
      if (status === 403 || status === 404) {
        setRevoked(true);
      } else {
        setError((err as Error).message);
      }
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [recipeId, enabled]);

  useEffect(() => {
    if (!enabled || revoked) return;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      await poll();
      timer = setTimeout(
        tick,
        document.hidden ? POLL_BACKGROUND_MS : POLL_FOREGROUND_MS,
      );
    };
    void tick();

    // Coming back to the tab should feel instant rather than waiting out a
    // background interval that may have just started.
    const onWake = () => { if (!document.hidden) void poll(); };
    document.addEventListener("visibilitychange", onWake);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [poll, enabled, revoked]);

  // Clear the unread badge once we've actually rendered what's there. Fire and
  // forget: failing to mark read is a cosmetic problem, not worth a toast.
  useEffect(() => {
    if (!summary || summary.unreadCount === 0) return;
    void ThreadsApi.markRead(recipeId, summary.revision).catch(() => {});
  }, [recipeId, summary?.revision, summary?.unreadCount]);

  return {
    comments, participants, summary, loading, error, revoked,
    refresh: () => { void poll(); },
  };
}
